import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { vaultDownloadName } from "../../lib/vault-file-downloads";
import { saveBlob } from "@/ajax/documents/saveBlob";
import { useTRPC, type RouterOutputs } from "@/trpc";
import {
  readEvidenceText,
  evidencePreviewFormat,
  type EvidenceText,
} from "../verification-evidence-preview/evidence-preview-content";
import type { ProviderReadScope } from "./types";

type EvidenceTarget =
  | { label: string; fileUploadId: string }
  /** A staff file attached to a PSV item; needs no check. */
  | { label: string; itemKey: string; itemFileId: string }
  | {
      label: string;
      snapshot: true;
      /** Required when the check seals several snapshots at once, such as
       * the state Medicaid exclusion check (one per state). */
      snapshotId?: string;
    };
type Selection = {
  target: EvidenceTarget;
  scopeKey: string;
  getUrl: (asAttachment: boolean) => Promise<EvidenceUrl>;
};
type EvidenceUrl = RouterOutputs["psv"]["getEvidenceUrl"];
export type EvidencePreview = {
  selection: Selection;
  metadata: EvidenceUrl | null;
  text: EvidenceText | null;
  isLoading: boolean;
  isDownloading: boolean;
  error: string | null;
  downloadError: string | null;
};

/** The only supported way to show PSV evidence (captured source files, sealed
 * snapshots, and staff item files). Pair it with
 * `VerificationEvidencePreview`; never `window.open` or link to the signed
 * storage URL. Reviewers compare evidence against the record without leaving
 * the review dialog, and downloads go through the preview's Download button. */
export function useOpenVerificationEvidence({
  scope,
  runId,
  checkId,
}: {
  scope: ProviderReadScope;
  runId: string;
  /** Required for every target except an item file. */
  checkId?: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const scopeKey = JSON.stringify([
    scope.organizationBusinessUnitId,
    scope.providerUserId,
    scope.expectedAffiliationId,
    scope.credentialViewId,
    runId,
    checkId,
  ]);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [preview, setPreview] = useState<EvidencePreview | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const activeDownload = useRef<AbortController | null>(null);

  async function getUrl({
    target,
    asAttachment,
  }: {
    target: EvidenceTarget;
    asAttachment: boolean;
  }) {
    if ("itemFileId" in target) {
      return queryClient.fetchQuery({
        ...trpc.psv.review.getFileUrl.queryOptions({
          ...scope,
          runId,
          itemKey: target.itemKey,
          fileId: target.itemFileId,
          asAttachment,
        }),
        staleTime: 0,
        gcTime: 0,
      });
    }
    if (!checkId) {
      throw new Error("This evidence needs a source check.");
    }
    const input = { ...scope, runId, checkId, asAttachment };
    if ("snapshot" in target) {
      return queryClient.fetchQuery({
        ...trpc.psv.getSnapshotEvidenceUrl.queryOptions({
          ...input,
          snapshotId: target.snapshotId,
        }),
        staleTime: 0,
        gcTime: 0,
      });
    }
    return queryClient.fetchQuery({
      ...trpc.psv.getEvidenceUrl.queryOptions({
        ...input,
        fileUploadId: target.fileUploadId,
      }),
      staleTime: 0,
      gcTime: 0,
    });
  }

  useEffect(() => {
    if (!selection) {
      return;
    }
    if (selection.scopeKey !== scopeKey) {
      setSelection(null);
      setPreview(null);
      return;
    }
    const controller = new AbortController();
    const getSelectedUrl = selection.getUrl;
    activeRequest.current = controller;
    async function load() {
      try {
        const metadata = await getSelectedUrl(false);
        if (controller.signal.aborted) {
          return;
        }
        setPreview((current) =>
          current?.selection === selection ? { ...current, metadata } : current,
        );
        let text: EvidenceText | null = null;
        if (evidencePreviewFormat(metadata.mediaType) === "text") {
          text = await readEvidenceText({
            url: metadata.url,
            mediaType: metadata.mediaType,
            signal: controller.signal,
          });
        }
        if (controller.signal.aborted) {
          return;
        }
        setPreview((current) =>
          current?.selection === selection
            ? { ...current, text, isLoading: false }
            : current,
        );
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        const message =
          error instanceof Error ? error.message : "Could not load evidence.";
        setPreview((current) =>
          current?.selection === selection
            ? {
                ...current,
                isLoading: false,
                error: message,
              }
            : current,
        );
      }
    }
    void load();
    return () => {
      controller.abort();
      activeDownload.current?.abort();
      activeDownload.current = null;
    };
  }, [selection, scopeKey]);

  function open(target: EvidenceTarget) {
    activeRequest.current?.abort();
    activeDownload.current?.abort();
    // Capture the request scope with the selection; rerenders must not retarget or restart it.
    const nextSelection = {
      target,
      scopeKey,
      getUrl: (asAttachment: boolean) => getUrl({ target, asAttachment }),
    };
    setSelection(nextSelection);
    setPreview({
      selection: nextSelection,
      metadata: null,
      text: null,
      isLoading: true,
      isDownloading: false,
      error: null,
      downloadError: null,
    });
  }
  function onClose() {
    activeRequest.current?.abort();
    activeDownload.current?.abort();
    setSelection(null);
    setPreview(null);
  }
  function onRetry() {
    if (selection?.scopeKey === scopeKey) {
      open(selection.target);
    }
  }
  async function onDownload() {
    if (
      !selection ||
      selection.scopeKey !== scopeKey ||
      activeDownload.current
    ) {
      return;
    }
    const controller = new AbortController();
    activeDownload.current = controller;
    setPreview((current) =>
      current?.selection === selection
        ? { ...current, isDownloading: true, downloadError: null }
        : current,
    );
    try {
      const { url, fileName } = await selection.getUrl(true);
      if (controller.signal.aborted) {
        return;
      }
      const response = await fetch(url, {
        credentials: "omit",
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error("Could not download evidence. Please try again.");
      }
      const blob = await response.blob();
      if (controller.signal.aborted) {
        return;
      }
      await saveBlob({ blob, fileName: vaultDownloadName(fileName) });
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      const message =
        error instanceof Error ? error.message : "Could not download evidence.";
      setPreview((current) =>
        current?.selection === selection
          ? {
              ...current,
              downloadError: message,
            }
          : current,
      );
    } finally {
      if (activeDownload.current === controller) {
        activeDownload.current = null;
      }
      if (!controller.signal.aborted) {
        setPreview((current) =>
          current?.selection === selection
            ? { ...current, isDownloading: false }
            : current,
        );
      }
    }
  }
  return {
    preview: preview?.selection.scopeKey === scopeKey ? preview : null,
    open,
    onClose,
    onRetry,
    onDownload,
  };
}
